import React, { useEffect, useMemo, useState, Children } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterIcon, SearchIcon, LayoutGridIcon, PencilIcon, FileTextIcon, BookOpenIcon, MegaphoneIcon, TagIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import GalleryView, { GalleryItem } from '../../components/Creative/GalleryView';
// Mock Data
const galleryItems: GalleryItem[] = [{
  id: 'sg1',
  title: 'Fantasy Novel Edit - Before/After',
  imageUrl: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600',
  creator: "Emma's Editing Services",
  price: 0.08,
  priceType: 'starting',
  serviceType: 'Editing Sample',
  genre: ['Fantasy', 'YA'],
  description: 'Sample showing developmental edits on a fantasy manuscript chapter.',
  aspectRatio: 'landscape'
}, {
  id: 'sg2',
  title: 'Interior Formatting - Sci-Fi',
  imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600',
  creator: 'Pristine Productions',
  price: 150,
  priceType: 'starting',
  serviceType: 'Book Formatting',
  genre: ['Sci-Fi'],
  description: 'Clean, modern interior layout for a science fiction novel.',
  aspectRatio: 'portrait'
}, {
  id: 'sg3',
  title: 'Romance Cover Design',
  imageUrl: 'https://images.unsplash.com/photo-1518182170546-0766aa6f6914?w=600',
  creator: 'Creative Covers',
  price: 300,
  priceType: 'fixed',
  serviceType: 'Cover Design',
  genre: ['Romance', 'Contemporary'],
  description: 'Custom illustrated cover for a contemporary romance.',
  aspectRatio: 'portrait'
}, {
  id: 'sg4',
  title: 'Social Media Kit',
  imageUrl: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600',
  creator: 'Marketing Mavericks',
  price: 100,
  priceType: 'starting',
  serviceType: 'Marketing Design',
  genre: ['Non-Fiction'],
  description: 'Set of branded social media templates for book launch.',
  aspectRatio: 'square'
}, {
  id: 'sg5',
  title: 'Historical Fiction Map',
  imageUrl: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600',
  creator: 'Pristine Productions',
  price: 200,
  priceType: 'commissioned',
  serviceType: 'Interior Layout',
  genre: ['Historical Fiction'],
  description: 'Custom map design for historical fiction interior.',
  aspectRatio: 'landscape'
}, {
  id: 'sg6',
  title: 'Line Edit Sample',
  imageUrl: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=600',
  creator: 'Clear Copy Editing',
  price: 0.04,
  priceType: 'starting',
  serviceType: 'Editing Sample',
  genre: ['Mystery'],
  description: 'Line editing sample demonstrating style improvements.',
  aspectRatio: 'portrait'
}, {
  id: 'sg7',
  title: 'Audiobook Marketing Teaser',
  imageUrl: 'https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?w=600',
  creator: 'Audiobook Artisans',
  price: 50,
  priceType: 'fixed',
  serviceType: 'Marketing Design',
  genre: ['Thriller'],
  description: 'Visual teaser graphic for audiobook release.',
  aspectRatio: 'square'
}, {
  id: 'sg8',
  title: "Children's Book Layout",
  imageUrl: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=600',
  creator: 'Pristine Productions',
  price: 250,
  priceType: 'starting',
  serviceType: 'Book Formatting',
  genre: ["Children's"],
  description: "Full color layout for illustrated children's book.",
  aspectRatio: 'landscape'
}];
const ServicesGalleryHub = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL Params
  const typeParam = searchParams.get('type') || 'all';
  const queryParam = searchParams.get('q') || '';
  // State
  const [activeTab, setActiveTab] = useState(typeParam);
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter States
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  // Derived options
  const allServiceTypes = Array.from(new Set(galleryItems.map(i => i.serviceType).filter(Boolean))) as string[];
  // Sync state with URL
  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
    if (queryParam && queryParam !== searchQuery) {
      setSearchQuery(queryParam);
    }
  }, [typeParam, queryParam]);
  const updateUrl = (type: string, query: string) => {
    const params: any = {};
    if (type && type !== 'all') params.type = type;
    if (query) params.q = query;
    setSearchParams(params);
  };
  const handleTabChange = (type: string) => {
    setActiveTab(type);
    updateUrl(type, searchQuery);
  };
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    updateUrl(activeTab, query);
  };
  // Filter Logic
  const filteredItems = useMemo(() => {
    let result = [...galleryItems];
    // 1. Filter by Tab (Category)
    if (activeTab !== 'all') {
      if (activeTab === 'editing') result = result.filter(i => i.serviceType === 'Editing Sample');else if (activeTab === 'formatting') result = result.filter(i => i.serviceType === 'Book Formatting' || i.serviceType === 'Interior Layout');else if (activeTab === 'marketing') result = result.filter(i => i.serviceType === 'Marketing Design');else if (activeTab === 'covers') result = result.filter(i => i.serviceType === 'Cover Design');
    }
    // 2. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => item.title.toLowerCase().includes(q) || item.creator.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q) || item.genre?.some(g => g.toLowerCase().includes(q)));
    }
    // 3. Filter by Price Range
    if (selectedPriceRange !== 'all') {
      if (selectedPriceRange === 'under100') {
        result = result.filter(item => item.price < 100);
      } else if (selectedPriceRange === '100to250') {
        result = result.filter(item => item.price >= 100 && item.price <= 250);
      } else if (selectedPriceRange === 'over250') {
        result = result.filter(item => item.price > 250);
      }
    }
    // 4. Filter by Service Type
    if (selectedServiceTypes.length > 0) {
      result = result.filter(item => item.serviceType && selectedServiceTypes.includes(item.serviceType));
    }
    // 5. Filter by Genre
    if (selectedGenres.length > 0) {
      result = result.filter(item => item.genre?.some(g => selectedGenres.includes(g)));
    }
    return result;
  }, [activeTab, searchQuery, selectedPriceRange, selectedServiceTypes, selectedGenres]);
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'price',
    title: 'Price Range',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Prices'
    }, {
      value: 'under100',
      label: 'Under $100'
    }, {
      value: '100to250',
      label: '$100 - $250'
    }, {
      value: 'over250',
      label: '$250+'
    }],
    value: selectedPriceRange,
    onChange: setSelectedPriceRange
  }, {
    id: 'serviceType',
    title: 'Service Type',
    type: 'checkbox',
    options: allServiceTypes.map(t => ({
      value: t,
      label: t
    })),
    value: selectedServiceTypes,
    onChange: val => setSelectedServiceTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Sci-Fi',
      label: 'Sci-Fi'
    }, {
      value: 'Mystery',
      label: 'Mystery'
    }],
    value: selectedGenres,
    onChange: val => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const handleResetFilters = () => {
    setSelectedPriceRange('all');
    setSelectedServiceTypes([]);
    setSelectedGenres([]);
    setSearchQuery('');
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: LayoutGridIcon
  }, {
    id: 'editing',
    label: 'Editing Samples',
    icon: PencilIcon
  }, {
    id: 'formatting',
    label: 'Formatting',
    icon: FileTextIcon
  }, {
    id: 'covers',
    label: 'Cover Design',
    icon: BookOpenIcon
  }, {
    id: 'marketing',
    label: 'Marketing Materials',
    icon: MegaphoneIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13634" data-mp="ejkmftoq3kd5">
      {/* Header */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-13635" data-mp="3e4pz8mt4nm7i">
        <div className="max-w-7xl mx-auto" data-id="element-13636" data-mp="3i61qsxvkahic">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-13637" data-mp="3i506e2snoiny">
            <div data-id="element-13638" data-mp="3nbo5kpgiohc0">
              <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-13639" data-mp="3s86apqfrehov">
                Services Gallery
              </h1>
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-13640" data-mp="5oyiwpret8t">
                Browse portfolio samples and visual work from service
                professionals.
              </p>
            </div>
            <div className="w-full md:w-96" data-id="element-13641" data-mp="34jvfp3wqvc8i">
              <SearchWithAutocomplete value={searchQuery} onChange={handleSearch} placeholder="Search gallery..." variant="pill" suggestions={[]} data-id="element-13642" data-mp="34gr6fs4f9qaz" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" data-id="element-13643" data-mp="3icbji6xgzeah">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${rt.bgDark} text-white border-transparent shadow-md` : `${dk.surface} ${dk.textSecondary} ${dk.border} hover:${dk.surfaceMuted}`}`} data-id="element-13644" data-mp="2i93u7nyi235d">
                  <Icon className="h-4 w-4" data-id="element-13645" data-mp="1zcvka6ujl43q" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13646" data-mp="1pmamglmp03lv">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-13647" data-mp="1knfy745yhmud">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-13648" data-mp="1glcke8vte65x">
            <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-13649" data-mp="1gn5r1o6fgqdx">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-13650" data-mp="15zzd8kbtmsbf" />
              Filters
            </button>
          </div>

          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-13651" data-mp="s1831902xqsk">
            <FilterSidebar title="Filters" sections={filterSections} onReset={handleResetFilters} searchable={false} data-id="element-13652" data-mp="1kqygdftw0onm" />
          </FilterSidebarWrapper>

          {/* Gallery Grid */}
          <div className="flex-1 min-w-0" data-id="element-13653" data-mp="1fx3qfunovdg3">
            <GalleryView items={filteredItems} title="" categoryName={activeTab === 'all' ? 'Service Portfolios' : tabs.find(t => t.id === activeTab)?.label || 'Work'} data-id="element-13654" data-mp="1fw2cnifagcuo" />
          </div>
        </div>
      </div>
    </div>;
};
export default ServicesGalleryHub;